import { Trans, t } from "@lingui/macro";
import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Checkbox from "components/Checkbox/Checkbox";
import Footer from "components/Footer/Footer";
import Modal from "components/Modal/Modal";
import Tooltip from "components/Tooltip/Tooltip";

import MlpManager from "sdk/abis/MlpManager.json";
import ReaderV2 from "sdk/abis/ReaderV2.json";
import RewardReader from "sdk/abis/RewardReader.json";
import RewardRouter from "sdk/abis/RewardRouter.json";
import Token from "sdk/abis/Token.json";
import Vault from "sdk/abis/Vault.json";
import Vester from "sdk/abis/Vester.json";

import cx from "classnames";
import { ARBITRUM, BSC, getChainName, getConstant } from "config/chains";
import { BASIS_POINTS_DIVISOR_BIGINT } from "config/factors";
import { SetPendingTransactions, useMoneyPrice, useTotalMoneyStaked, useTotalMoneySupply } from "domain/legacy";
import { ethers } from "ethers";
import {
  MLP_DECIMALS,
  PLACEHOLDER_ACCOUNT,
  getBalanceAndSupplyData,
  getDepositBalanceData,
  getPageTitle,
  getProcessedData,
  getStakingData,
  ProcessedData,
} from "lib/legacy";
import { USD_DECIMALS } from "config/factors";
import { MARKETS } from "config/static/markets";
import { Link } from "react-router-dom";

import useSWR from "swr";

import { getContract } from "config/contracts";

import { useConnectModal } from "@rainbow-me/rainbowkit";
import { AlertInfo } from "components/AlertInfo/AlertInfo";
import { ApproveTokenButton } from "components/ApproveTokenButton/ApproveTokenButton";
import Button from "components/Button/Button";
import BuyInputSection from "components/BuyInputSection/BuyInputSection";
import SEO from "components/Common/SEO";
import ExternalLink from "components/ExternalLink/ExternalLink";
import PageTitle from "components/PageTitle/PageTitle";
import MONEYAprTooltip from "components/Stake/MONEYAprTooltip";
import ChainsStatsTooltipRow from "components/StatsTooltip/ChainsStatsTooltipRow";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import UserIncentiveDistributionList from "components/Synthetics/UserIncentiveDistributionList/UserIncentiveDistributionList";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";
import { getServerUrl } from "config/backend";
import { getIsSyntheticsSupported } from "config/features";
import { getIcons } from "config/icons";
import { getIncentivesV2Url } from "config/links";
import { NATIVE_TOKEN_ADDRESS } from "config/tokens";
import { MLP_PRICE_DECIMALS, MAX_METAMASK_MOBILE_DECIMALS } from "config/ui";
import useIncentiveStats from "domain/synthetics/common/useIncentiveStats";
import { useGovTokenAmount } from "domain/synthetics/governance/useGovTokenAmount";
import { useGovTokenDelegates } from "domain/synthetics/governance/useGovTokenDelegates";
import { getTotalGmInfo, useMarketTokensData } from "domain/synthetics/markets";
import { useGmMarketsApy } from "domain/synthetics/markets/useGmMarketsApy";
import { useTokensAllowanceData } from "domain/synthetics/tokens";
import { useAnyAirdroppedTokenTitle } from "domain/synthetics/tokens/useAirdroppedTokenTitle";
import { approveTokens } from "domain/tokens";
import useVestingData from "domain/vesting/useVestingData";
import { bigMath } from "lib/bigmath";
import { useChainId } from "lib/chains";
import { callContract, contractFetcher } from "lib/contracts";
import { helperToast } from "lib/helperToast";
import { useENS } from "lib/legacy";
import { useLocalStorageSerializeKey } from "lib/localStorage";
import {
  BN_ZERO,
  expandDecimals,
  formatAmount,
  formatAmountFree,
  formatKeyAmount,
  limitDecimals,
  parseValue,
} from "lib/numbers";
import { UncheckedJsonRpcSigner } from "lib/rpc/UncheckedJsonRpcSigner";
import { usePendingTxns } from "lib/usePendingTxns";
import { shortenAddressOrEns } from "lib/wallets";
import useIsMetamaskMobile from "lib/wallets/useIsMetamaskMobile";
import useWallet from "lib/wallets/useWallet";
import "./StakeV2.css";
import { MONEY_DAO_LINKS, getMoneyDAODelegateLink } from "./constants";
import { GmList } from "components/Synthetics/GmList/GmList";

const { ZeroAddress } = ethers;

function StakeModal(props: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  chainId: number;
  title: string;
  maxAmount: bigint | undefined;
  value: string;
  setValue: (value: string) => void;
  signer: UncheckedJsonRpcSigner | undefined;
  stakingTokenSymbol: string;
  stakingTokenAddress: string;
  farmAddress: string;
  rewardRouterAddress: string;
  stakeMethodName: string;
  setPendingTxns: SetPendingTransactions;
  processedData: ProcessedData | undefined;
}) {
  const {
    isVisible,
    setIsVisible,
    chainId,
    title,
    maxAmount,
    value,
    setValue,
    signer,
    stakingTokenSymbol,
    stakingTokenAddress,
    farmAddress,
    rewardRouterAddress,
    stakeMethodName,
    setPendingTxns,
    processedData,
  } = props;

  // const govTokenAmount = useGovTokenAmount(chainId);
  // const govTokenDelegatesAddress = useGovTokenDelegates(chainId);
  // const isUndelegatedGovToken =
  //   chainId === ARBITRUM && govTokenDelegatesAddress === NATIVE_TOKEN_ADDRESS && govTokenAmount && govTokenAmount > 0;

  const [isStaking, setIsStaking] = useState(false);
  const isMetamaskMobile = useIsMetamaskMobile();
  const [isApproving, setIsApproving] = useState(false);
  const icons = getIcons(chainId);
  const { tokensAllowanceData } = useTokensAllowanceData(chainId, {
    spenderAddress: farmAddress,
    tokenAddresses: [stakingTokenAddress].filter(Boolean),
  });
  const tokenAllowance = tokensAllowanceData?.[stakingTokenAddress];

  let amount = parseValue(value, 18);
  const needApproval =
    farmAddress !== ZeroAddress && tokenAllowance !== undefined && amount !== undefined && amount > tokenAllowance;

  let stakeBonusPercentage: undefined | bigint = undefined;
  if (
    processedData &&
    amount !== undefined &&
    amount > 0 &&
    processedData.esMoneyInStakedMoney !== undefined &&
    processedData.moneyInStakedMoney !== undefined
  ) {
    const divisor = processedData.esMoneyInStakedMoney + processedData.moneyInStakedMoney;
    if (divisor !== 0n) {
      stakeBonusPercentage = bigMath.mulDiv(amount, BASIS_POINTS_DIVISOR_BIGINT, divisor);
    }
  }

  const getError = () => {
    if (amount === undefined || amount === 0n) {
      return t`Enter an amount`;
    }
    if (maxAmount !== undefined && amount > maxAmount) {
      return t`Max amount exceeded`;
    }
  };

  const onClickPrimary = () => {
    if (needApproval) {
      approveTokens({
        setIsApproving,
        signer,
        tokenAddress: stakingTokenAddress,
        spender: farmAddress,
        chainId,
      });
      return;
    }

    setIsStaking(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, signer);

    callContract(chainId, contract, stakeMethodName, [amount], {
      sentMsg: t`Stake submitted!`,
      failMsg: t`Stake failed.`,
      setPendingTxns,
    })
      .then(() => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsStaking(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isApproving || needApproval) {
      return false;
    }
    if (isStaking) {
      return false;
    }
    // if (isUndelegatedGovToken) {
    //   return false;
    // }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isApproving || needApproval) {
      return t`Pending ${stakingTokenSymbol} approval`;
    }
    if (isStaking) {
      return t`Staking...`;
    }
    return t`Stake`;
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title}>
        <BuyInputSection
          topLeftLabel={t`Stake`}
          topRightLabel={t`Max`}
          topRightValue={formatAmount(maxAmount, 18, 4, true)}
          onClickTopRightLabel={() => {
            if (maxAmount === undefined) return;
            const formattedMaxAmount = formatAmountFree(maxAmount, 18, 18);
            const finalMaxAmount = isMetamaskMobile
              ? limitDecimals(formattedMaxAmount, MAX_METAMASK_MOBILE_DECIMALS)
              : formattedMaxAmount;
            setValue(finalMaxAmount);
          }}
          inputValue={value}
          onInputValueChange={(e) => setValue(e.target.value)}
          showMaxButton={false}
        >
          <div className="Stake-modal-icons">
            <img
              className="icon mr-5 h-22"
              height="22"
              src={icons?.[stakingTokenSymbol.toLowerCase()]}
              alt={stakingTokenSymbol}
            />
            {stakingTokenSymbol}
          </div>
        </BuyInputSection>

        {(needApproval || isApproving) && (
          <div className="mb-12">
            <ApproveTokenButton
              tokenAddress={stakingTokenAddress}
              spenderAddress={farmAddress}
              tokenSymbol={stakingTokenSymbol}
              isApproved={!needApproval}
            />
          </div>
        )}

        {stakeBonusPercentage !== undefined &&
          stakeBonusPercentage > 0 &&
          amount !== undefined &&
          maxAmount !== undefined &&
          amount <= maxAmount && (
            <AlertInfo type="info">
              <Trans>You will earn {formatAmount(stakeBonusPercentage, 2, 2)}% more rewards with this action.</Trans>
            </AlertInfo>
          )}

        {/* {isUndelegatedGovToken ? (
          <AlertInfo type="warning" className={cx("DelegateMONEYAlertInfo")} textColor="text-yellow-500">
            <Trans>
              <ExternalLink href={MONEY_DAO_LINKS.VOTING_POWER} className="display-inline">
                Delegate your undelegated {formatAmount(govTokenAmount, 18, 2, true)} MONEY DAO
              </ExternalLink>{" "}
              voting power before staking.
            </Trans>
          </AlertInfo>
        ) : null} */}

        <div className="Exchange-swap-button-container">
          <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function UnstakeModal(props: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  chainId: number;
  title: string;
  maxAmount: bigint | undefined;
  value: string;
  setValue: (value: string) => void;
  signer: UncheckedJsonRpcSigner | undefined;
  unstakingTokenSymbol: string;
  rewardRouterAddress: string;
  unstakeMethodName: string;
  reservedAmount: bigint | undefined;
  setPendingTxns: SetPendingTransactions;
  processedData: ProcessedData | undefined;
}) {
  const {
    isVisible,
    setIsVisible,
    chainId,
    title,
    maxAmount,
    value,
    setValue,
    signer,
    unstakingTokenSymbol,
    rewardRouterAddress,
    unstakeMethodName,
    reservedAmount,
    setPendingTxns,
    processedData,
  } = props;
  const [isUnstaking, setIsUnstaking] = useState(false);
  const icons = getIcons(chainId);

  let amount = parseValue(value, 18);

  let unstakeBonusLostPercentage: undefined | bigint = undefined;
  if (
    processedData &&
    amount !== undefined &&
    amount > 0 &&
    processedData.esMoneyInStakedMoney !== undefined &&
    processedData.moneyInStakedMoney !== undefined
  ) {
    const divisor = processedData.esMoneyInStakedMoney + processedData.moneyInStakedMoney;
    if (divisor !== 0n) {
      unstakeBonusLostPercentage = bigMath.mulDiv(amount, BASIS_POINTS_DIVISOR_BIGINT, divisor);
    }
  }

  const votingPowerBurnAmount = amount;

  const getError = () => {
    if (amount === undefined || amount === 0n) {
      return t`Enter an amount`;
    }
    if (maxAmount !== undefined && amount > maxAmount) {
      return t`Max amount exceeded`;
    }
  };

  const onClickPrimary = () => {
    setIsUnstaking(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, signer);
    callContract(chainId, contract, unstakeMethodName, [amount], {
      sentMsg: t`Unstake submitted!`,
      failMsg: t`Unstake failed.`,
      successMsg: t`Unstake completed!`,
      setPendingTxns,
    })
      .then(() => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsUnstaking(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isUnstaking) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isUnstaking) {
      return t`Unstaking...`;
    }
    return t`Unstake`;
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title}>
        <BuyInputSection
          topLeftLabel={t`Unstake`}
          topRightLabel={t`Max`}
          topRightValue={formatAmount(maxAmount, 18, 4, true)}
          onClickTopRightLabel={() => maxAmount !== undefined && setValue(formatAmountFree(maxAmount, 18, 18))}
          inputValue={value}
          onInputValueChange={(e) => setValue(e.target.value)}
          showMaxButton={false}
        >
          <div className="Stake-modal-icons">
            <img
              className="icon mr-5 h-22"
              height="22"
              src={icons?.[unstakingTokenSymbol.toLowerCase()]}
              alt={unstakingTokenSymbol}
            />
            {unstakingTokenSymbol}
          </div>
        </BuyInputSection>
        {reservedAmount !== undefined && reservedAmount > 0 && (
          <AlertInfo type="info">
            You have {formatAmount(reservedAmount, 18, 2, true)} tokens reserved for vesting.
          </AlertInfo>
        )}
        {unstakeBonusLostPercentage !== undefined &&
          unstakeBonusLostPercentage > 0 &&
          amount !== undefined &&
          maxAmount !== undefined &&
          amount <= maxAmount && (
            <AlertInfo type="warning">
              <Trans>
                {chainId === ARBITRUM ? (
                  <span>
                    Unstaking will burn {formatAmount(votingPowerBurnAmount, 18, 2, true)} voting power.&nbsp;
                  </span>
                ) : null}
                <span>
                  You will earn {formatAmount(unstakeBonusLostPercentage, 2, 2)}% less rewards with this action.
                </span>
              </Trans>
            </AlertInfo>
          )}
        <div className="Exchange-swap-button-container">
          <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function VesterDepositModal(props: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  chainId: number;
  title: string;
  maxAmount: bigint | undefined;
  value: string;
  setValue: (value: string) => void;
  balance: bigint | undefined;
  vestedAmount: bigint | undefined;
  averageStakedAmount: bigint | undefined;
  maxVestableAmount: bigint | undefined;
  signer: UncheckedJsonRpcSigner | undefined;
  stakeTokenLabel: string;
  reserveAmount: bigint | undefined;
  maxReserveAmount: bigint | undefined;
  vesterAddress: string;
  setPendingTxns: SetPendingTransactions;
}) {
  const {
    isVisible,
    setIsVisible,
    chainId,
    title,
    maxAmount,
    value,
    setValue,
    balance,
    vestedAmount,
    averageStakedAmount,
    maxVestableAmount,
    signer,
    stakeTokenLabel,
    reserveAmount,
    maxReserveAmount,
    vesterAddress,
    setPendingTxns,
  } = props;
  const [isDepositing, setIsDepositing] = useState(false);
  const icons = getIcons(chainId);

  let amount = parseValue(value, 18);

  let nextReserveAmount: bigint | undefined = reserveAmount;

  let nextDepositAmount = vestedAmount;
  if (amount !== undefined && vestedAmount !== undefined) {
    nextDepositAmount = vestedAmount + amount;
  }

  let additionalReserveAmount = 0n;
  if (
    amount !== undefined &&
    nextDepositAmount !== undefined &&
    averageStakedAmount !== undefined &&
    maxVestableAmount !== undefined &&
    maxVestableAmount > 0 &&
    nextReserveAmount !== undefined
  ) {
    nextReserveAmount = bigMath.mulDiv(nextDepositAmount, averageStakedAmount, maxVestableAmount);
    if (reserveAmount !== undefined && nextReserveAmount > reserveAmount) {
      additionalReserveAmount = nextReserveAmount - reserveAmount;
    }
  }

  const getError = () => {
    if (amount === undefined) {
      return t`Enter an amount`;
    }
    if (maxAmount !== undefined && amount > maxAmount) {
      return t`Max amount exceeded`;
    }
    if (maxReserveAmount !== undefined && nextReserveAmount !== undefined && nextReserveAmount > maxReserveAmount) {
      return t`Insufficient staked tokens`;
    }
  };

  const onClickPrimary = () => {
    setIsDepositing(true);
    const contract = new ethers.Contract(vesterAddress, Vester.abi, signer);

    callContract(chainId, contract, "deposit", [amount], {
      sentMsg: t`Deposit submitted!`,
      failMsg: t`Deposit failed!`,
      successMsg: t`Deposited!`,
      setPendingTxns,
    })
      .then(() => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsDepositing(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isDepositing) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isDepositing) {
      return t`Depositing...`;
    }
    return t`Deposit`;
  };

  return (
    <SEO title={getPageTitle(t`Earn`)}>
      <div className="StakeModal">
        <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title} className="non-scrollable">
          <BuyInputSection
            topLeftLabel={t`Deposit`}
            topRightLabel={t`Max`}
            topRightValue={formatAmount(maxAmount, 18, 4, true)}
            onClickTopRightLabel={() => maxAmount !== undefined && setValue(formatAmountFree(maxAmount, 18, 18))}
            inputValue={value}
            onInputValueChange={(e) => setValue(e.target.value)}
            showMaxButton={false}
          >
            <div className="Stake-modal-icons">
              <img className="icon mr-5 h-22" height="22" src={icons?.esmoney} alt="esMONEY" />
              esMONEY
            </div>
          </BuyInputSection>

          <div className="VesterDepositModal-info-rows">
            <div className="Exchange-info-row">
              <div className="Exchange-info-label">
                <Trans>Wallet</Trans>
              </div>
              <div className="align-right">{formatAmount(balance, 18, 2, true)} esMONEY</div>
            </div>
            <div className="Exchange-info-row">
              <div className="Exchange-info-label">
                <Trans>Vault Capacity</Trans>
              </div>
              <div className="align-right">
                <TooltipWithPortal
                  handle={`${formatAmount(nextDepositAmount, 18, 2, true)} / ${formatAmount(
                    maxVestableAmount,
                    18,
                    2,
                    true
                  )}`}
                  position="top-end"
                  renderContent={() => {
                    return (
                      <div>
                        <p className="text-white">
                          <Trans>Vault Capacity for your Account:</Trans>
                        </p>
                        <br />
                        <StatsTooltipRow
                          showDollar={false}
                          label={t`Deposited`}
                          value={`${formatAmount(vestedAmount, 18, 2, true)} esMONEY`}
                        />
                        <StatsTooltipRow
                          showDollar={false}
                          label={t`Max Capacity`}
                          value={`${formatAmount(maxVestableAmount, 18, 2, true)} esMONEY`}
                        />
                      </div>
                    );
                  }}
                />
              </div>
            </div>
            {reserveAmount !== undefined && (
              <div className="Exchange-info-row">
                <div className="Exchange-info-label">
                  <Trans>Reserve Amount</Trans>
                </div>
                <div className="align-right">
                  <TooltipWithPortal
                    handle={`${formatAmount(
                      nextReserveAmount,
                      18,
                      2,
                      true
                    )} / ${formatAmount(maxReserveAmount, 18, 2, true)}`}
                    position="top-end"
                    renderContent={() => {
                      return (
                        <>
                          <StatsTooltipRow
                            label={t`Current Reserved`}
                            value={formatAmount(reserveAmount, 18, 2, true)}
                            showDollar={false}
                          />
                          <StatsTooltipRow
                            label={t`Additional reserve required`}
                            value={formatAmount(additionalReserveAmount, 18, 2, true)}
                            showDollar={false}
                          />
                          {amount !== undefined &&
                            nextReserveAmount !== undefined &&
                            maxReserveAmount !== undefined &&
                            nextReserveAmount > maxReserveAmount && (
                              <>
                                <br />
                                <Trans>
                                  You need a total of at least {formatAmount(nextReserveAmount, 18, 2, true)}{" "}
                                  {stakeTokenLabel} to vest {formatAmount(amount, 18, 2, true)} esMONEY.
                                </Trans>
                              </>
                            )}
                        </>
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="Exchange-swap-button-container">
            <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
              {getPrimaryText()}
            </Button>
          </div>
        </Modal>
      </div>
    </SEO>
  );
}

function VesterWithdrawModal(props: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  chainId: number;
  title: string;
  signer: UncheckedJsonRpcSigner | undefined;
  vesterAddress: string;
  setPendingTxns: SetPendingTransactions;
}) {
  const { isVisible, setIsVisible, chainId, title, signer, vesterAddress, setPendingTxns } = props;
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const onClickPrimary = () => {
    setIsWithdrawing(true);
    const contract = new ethers.Contract(vesterAddress, Vester.abi, signer);

    callContract(chainId, contract, "withdraw", [], {
      sentMsg: t`Withdraw submitted.`,
      failMsg: t`Withdraw failed.`,
      successMsg: t`Withdrawn!`,
      setPendingTxns,
    })
      .then(() => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsWithdrawing(false);
      });
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title}>
        <Trans>
          <div>
            This will withdraw and unreserve all tokens as well as pause vesting.
            <br />
            <br />
            esMONEY tokens that have been converted to MONEY will be claimed and remain as MONEY tokens.
            <br />
            <br />
            To claim MONEY tokens without withdrawing, use the "Claim" button under the Total Rewards section.
            <br />
            <br />
          </div>
        </Trans>
        <div className="Exchange-swap-button-container">
          <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={isWithdrawing}>
            {!isWithdrawing && "Confirm Withdraw"}
            {isWithdrawing && "Confirming..."}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function AffiliateVesterWithdrawModal(props) {
  const { isVisible, setIsVisible, chainId, signer, setPendingTxns } = props;
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const affiliateVesterAddress = getContract(chainId, "AffiliateVester");

  const onClickPrimary = () => {
    setIsWithdrawing(true);
    const contract = new ethers.Contract(affiliateVesterAddress, Vester.abi, signer);

    callContract(chainId, contract, "withdraw", [], {
      sentMsg: t`Withdraw submitted.`,
      failMsg: t`Withdraw failed.`,
      successMsg: t`Withdrawn!`,
      setPendingTxns,
    })
      .then(() => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsWithdrawing(false);
      });
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={t`Withdraw from Affiliate Vault`}>
        <Trans>
          <div>
            This will withdraw all esMONEY tokens as well as pause vesting.
            <br />
            <br />
            esMONEY tokens that have been converted to MONEY will be claimed and remain as MONEY tokens.
            <br />
            <br />
            To claim MONEY tokens without withdrawing, use the "Claim" button.
            <br />
            <br />
          </div>
        </Trans>
        <div className="Exchange-swap-button-container">
          <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={isWithdrawing}>
            {!isWithdrawing && "Confirm Withdraw"}
            {isWithdrawing && "Confirming..."}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function ClaimModal(props: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  rewardRouterAddress: string;
  signer: UncheckedJsonRpcSigner | undefined;
  chainId: number;
  setPendingTxns: SetPendingTransactions;
  totalMoneyRewards: bigint | undefined;
  nativeTokenSymbol: string;
  wrappedTokenSymbol: string;
  isNativeTokenToClaim?: boolean;
  moneyUsageOptionsMsg?: React.ReactNode;
  onClaimSuccess?: () => void;
}) {
  const {
    isVisible,
    setIsVisible,
    rewardRouterAddress,
    signer,
    chainId,
    setPendingTxns,
    totalMoneyRewards,
    nativeTokenSymbol,
    wrappedTokenSymbol,
    isNativeTokenToClaim,
    moneyUsageOptionsMsg,
    onClaimSuccess,
  } = props;
  const [isClaiming, setIsClaiming] = useState(false);
  const [shouldClaimMoney, setShouldClaimMoney] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-money"],
    true
  );
  const [shouldStakeMoney, setShouldStakeMoney] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-money"],
    true
  );
  const [shouldClaimEsMoney, setShouldClaimEsMoney] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-es-money"],
    true
  );
  const [shouldStakeEsMoney, setShouldStakeEsMoney] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-es-money"],
    true
  );
  const [shouldClaimWeth, setShouldClaimWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-weth"],
    true
  );
  const [shouldConvertWeth, setShouldConvertWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-convert-weth"],
    true
  );

  // const govTokenAmount = useGovTokenAmount(chainId);
  // const govTokenDelegatesAddress = useGovTokenDelegates(chainId);
  // const isUndelegatedGovToken =
  //   chainId === ARBITRUM && govTokenDelegatesAddress === NATIVE_TOKEN_ADDRESS && govTokenAmount && govTokenAmount > 0;

  const moneyAddress = getContract(chainId, "MONEY");
  const stakedMoneyTrackerAddress = getContract(chainId, "StakedMoneyTracker");

  const [isApproving, setIsApproving] = useState(false);

  const { tokensAllowanceData: moneyAllowanceData } = useTokensAllowanceData(chainId, {
    spenderAddress: stakedMoneyTrackerAddress,
    tokenAddresses: [moneyAddress],
  });

  const moneyTokenAllowance = moneyAllowanceData?.[moneyAddress];

  const needApproval =
    shouldStakeMoney &&
    totalMoneyRewards !== undefined &&
    ((moneyTokenAllowance !== undefined && totalMoneyRewards > moneyTokenAllowance) ||
      (totalMoneyRewards > 0n && moneyTokenAllowance === undefined));

  const isPrimaryEnabled = () => {
    // return !isClaiming && !isApproving && !needApproval && !isUndelegatedGovToken;
    return !isClaiming && !isApproving && !needApproval;
  };

  const getPrimaryText = () => {
    if (needApproval || isApproving) {
      return t`Pending MONEY approval`;
    }

    if (isClaiming) {
      return t`Claiming...`;
    }
    return t`Claim`;
  };

  const onClickPrimary = () => {
    if (needApproval) {
      approveTokens({
        setIsApproving,
        signer,
        tokenAddress: moneyAddress,
        spender: stakedMoneyTrackerAddress,
        chainId,
      });
      return;
    }

    setIsClaiming(true);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, signer);
    callContract(
      chainId,
      contract,
      "handleRewards",
      [
        shouldClaimMoney || shouldStakeMoney,
        shouldStakeMoney,
        shouldClaimEsMoney || shouldStakeEsMoney,
        shouldStakeEsMoney,
        true,
        isNativeTokenToClaim ? shouldClaimWeth || shouldConvertWeth : false,
        isNativeTokenToClaim ? shouldConvertWeth : false,
      ],
      {
        sentMsg: t`Claim submitted!`,
        failMsg: t`Claim failed.`,
        successMsg: t`Claim completed!`,
        successDetailsMsg: !shouldStakeMoney ? moneyUsageOptionsMsg : undefined,
        setPendingTxns,
      }
    )
      .then(() => {
        setIsVisible(false);
        onClaimSuccess?.();
      })
      .finally(() => {
        setIsClaiming(false);
      });
  };

  const toggleShouldStakeMoney = (value) => {
    if (value) {
      setShouldClaimMoney(true);
    }
    setShouldStakeMoney(value);
  };

  const toggleShouldStakeEsMoney = (value) => {
    if (value) {
      setShouldClaimEsMoney(true);
    }
    setShouldStakeEsMoney(value);
  };

  const toggleConvertWeth = (value) => {
    if (value) {
      setShouldClaimWeth(true);
    }
    setShouldConvertWeth(value);
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={t`Claim Rewards`}>
        <div className="CompoundModal-menu">
          <div>
            <Checkbox isChecked={shouldClaimMoney} setIsChecked={setShouldClaimMoney} disabled={shouldStakeMoney}>
              <Trans>Claim MONEY Rewards</Trans>
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldStakeMoney} setIsChecked={toggleShouldStakeMoney}>
              <Trans>Stake MONEY Rewards</Trans>
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldClaimEsMoney} setIsChecked={setShouldClaimEsMoney} disabled={shouldStakeEsMoney}>
              <Trans>Claim esMONEY Rewards</Trans>
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldStakeEsMoney} setIsChecked={toggleShouldStakeEsMoney}>
              <Trans>Stake esMONEY Rewards</Trans>
            </Checkbox>
          </div>
          {isNativeTokenToClaim && (
            <>
              <div>
                <Checkbox isChecked={shouldClaimWeth} setIsChecked={setShouldClaimWeth} disabled={shouldConvertWeth}>
                  <Trans>Claim {wrappedTokenSymbol} Rewards</Trans>
                </Checkbox>
              </div>
              <div>
                <Checkbox isChecked={shouldConvertWeth} setIsChecked={toggleConvertWeth}>
                  <Trans>
                    Convert {wrappedTokenSymbol} to {nativeTokenSymbol}
                  </Trans>
                </Checkbox>
              </div>
            </>
          )}
        </div>
        {(needApproval || isApproving) && (
          <div className="mb-12">
            <ApproveTokenButton
              tokenAddress={moneyAddress}
              spenderAddress={stakedMoneyTrackerAddress}
              tokenSymbol={"MONEY"}
              isApproved={!needApproval}
            />
          </div>
        )}
        {/* {isUndelegatedGovToken ? (
          <AlertInfo type="warning" className={cx("DelegateMONEYAlertInfo")} textColor="text-yellow-500">
            <Trans>
              <ExternalLink href={MONEY_DAO_LINKS.VOTING_POWER} className="display-inline">
                Delegate your undelegated {formatAmount(govTokenAmount, 18, 2, true)} MONEY DAO
              </ExternalLink>
              voting power before claiming.
            </Trans>
          </AlertInfo>
        ) : null} */}
        <div className="Exchange-swap-button-container">
          <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function AffiliateClaimModal(props: {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  signer: UncheckedJsonRpcSigner | undefined;
  chainId: number;
  setPendingTxns: SetPendingTransactions;
  totalVesterRewards: bigint | undefined;
}) {
  const { isVisible, setIsVisible, signer, chainId, setPendingTxns, totalVesterRewards } = props;
  const [isClaiming, setIsClaiming] = useState(false);
  const affiliateVesterAddress = getContract(chainId, "AffiliateVester");

  const isPrimaryEnabled = () => {
    if (totalVesterRewards == undefined || totalVesterRewards == 0n) {
      return false;
    }

    return !isClaiming;
  };

  const getPrimaryText = () => {
    if (isClaiming) {
      return t`Claiming...`;
    }
    return t`Claim`;
  };

  const onClickPrimary = () => {
    setIsClaiming(true);

    const affiliateVesterContract = new ethers.Contract(affiliateVesterAddress, Vester.abi, signer);

    callContract(chainId, affiliateVesterContract, "claim", [], {
      sentMsg: t`Claim submitted.`,
      failMsg: t`Claim failed.`,
      successMsg: t`Claim completed!`,
      setPendingTxns,
    })
      .then(() => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsClaiming(false);
      });
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={t`Claim Affiliate Vault Rewards`}>
        <Trans>
          <div>
            This will claim {formatAmount(totalVesterRewards, 18, 4, true)} MONEY.
            <br />
            <br />
            After claiming, you can stake these MONEY tokens by using the "Stake" button in the MONEY section of this Earn
            page.
            <br />
            <br />
          </div>
        </Trans>
        <div className="Exchange-swap-button-container">
          <Button variant="primary-action" className="w-full" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default function StakeV2() {
  const { active, signer, account } = useWallet();
  const { chainId } = useChainId();
  const { openConnectModal } = useConnectModal();
  const incentiveStats = useIncentiveStats(chainId);
  const incentivesMessage = useMemo(() => {
    const bscLink = (
      <ExternalLink newTab href={getIncentivesV2Url(BSC)}>
        {getChainName(BSC)}
      </ExternalLink>
    );
    if (incentiveStats?.lp?.isActive && incentiveStats?.trading?.isActive) {
      return (
        <div>
          <Trans>Liquidity and trading incentives programs are live on {bscLink}.</Trans>
        </div>
      );
    } else if (incentiveStats?.lp?.isActive) {
      return (
        <div>
          <Trans>Liquidity incentives program is live on {bscLink}.</Trans>
        </div>
      );
    } else if (incentiveStats?.trading?.isActive) {
      return (
        <div>
          <Trans>Trading incentives program is live on {bscLink}.</Trans>
        </div>
      );
    }
  }, [incentiveStats?.lp?.isActive, incentiveStats?.trading?.isActive]);

  const incentivesToken = useAnyAirdroppedTokenTitle();

  const [, setPendingTxns] = usePendingTxns();

  const icons = getIcons(chainId);
  const hasInsurance = false;
  const [isStakeMoneyModalVisible, setIsStakeMoneyModalVisible] = useState(false);
  const [stakeMoneyValue, setStakeMoneyValue] = useState("");
  const [isStakeEsMoneyModalVisible, setIsStakeEsMoneyModalVisible] = useState(false);
  const [stakeEsMoneyValue, setStakeEsMoneyValue] = useState("");

  const [isUnstakeModalVisible, setIsUnstakeModalVisible] = useState(false);
  const [unstakeModalTitle, setUnstakeModalTitle] = useState("");
  const [unstakeModalMaxAmount, setUnstakeModalMaxAmount] = useState<bigint | undefined>(undefined);
  const [unstakeValue, setUnstakeValue] = useState("");
  const [unstakingTokenSymbol, setUnstakingTokenSymbol] = useState("");
  const [unstakeMethodName, setUnstakeMethodName] = useState("");

  const [isVesterDepositModalVisible, setIsVesterDepositModalVisible] = useState(false);
  const [vesterDepositTitle, setVesterDepositTitle] = useState("");
  const [vesterDepositStakeTokenLabel, setVesterDepositStakeTokenLabel] = useState("");
  const [vesterDepositMaxAmount, setVesterDepositMaxAmount] = useState<bigint | undefined>();
  const [vesterDepositBalance, setVesterDepositBalance] = useState<bigint | undefined>();
  const [vesterDepositVestedAmount, setVesterDepositVestedAmount] = useState<bigint | undefined>();
  const [vesterDepositAverageStakedAmount, setVesterDepositAverageStakedAmount] = useState<bigint | undefined | string>(
    ""
  );
  const [vesterDepositMaxVestableAmount, setVesterDepositMaxVestableAmount] = useState<bigint | undefined>();
  const [vesterDepositValue, setVesterDepositValue] = useState("");
  const [vesterDepositReserveAmount, setVesterDepositReserveAmount] = useState<bigint | undefined>();
  const [vesterDepositMaxReserveAmount, setVesterDepositMaxReserveAmount] = useState<bigint | undefined>();
  const [vesterDepositAddress, setVesterDepositAddress] = useState("");

  const [isVesterWithdrawModalVisible, setIsVesterWithdrawModalVisible] = useState(false);
  const [isAffiliateVesterWithdrawModalVisible, setIsAffiliateVesterWithdrawModalVisible] = useState(false);
  const [vesterWithdrawTitle, setVesterWithdrawTitle] = useState("");
  const [vesterWithdrawAddress, setVesterWithdrawAddress] = useState("");

  const [isCompoundModalVisible, setIsCompoundModalVisible] = useState(false);
  const [isAffiliateClaimModalVisible, setIsAffiliateClaimModalVisible] = useState(false);

  const rewardRouterAddress = getContract(chainId, "RewardRouter");
  const rewardReaderAddress = getContract(chainId, "RewardReader");
  const readerAddress = getContract(chainId, "Reader");

  const vaultAddress = getContract(chainId, "Vault");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const moneyAddress = getContract(chainId, "MONEY");
  const esMoneyAddress = getContract(chainId, "ES_MONEY");
  const bnMoneyAddress = getContract(chainId, "BN_MONEY");
  const mlpAddress = getContract(chainId, "MLP");

  const stakedMoneyTrackerAddress = getContract(chainId, "StakedMoneyTracker");
  const bonusMoneyTrackerAddress = getContract(chainId, "BonusMoneyTracker");
  const feeMoneyTrackerAddress = getContract(chainId, "FeeMoneyTracker");

  const stakedMlpTrackerAddress = getContract(chainId, "StakedMlpTracker");
  const feeMlpTrackerAddress = getContract(chainId, "FeeMlpTracker");
  const extendedMoneyTrackerAddress = getContract(chainId, "ExtendedMoneyTracker");

  const mlpManagerAddress = getContract(chainId, "MlpManager");

  const stakedMoneyDistributorAddress = getContract(chainId, "StakedMoneyDistributor");
  const stakedMlpDistributorAddress = getContract(chainId, "StakedMlpDistributor");

  const moneyVesterAddress = getContract(chainId, "MoneyVester");
  const mlpVesterAddress = getContract(chainId, "MlpVester");
  const affiliateVesterAddress = getContract(chainId, "AffiliateVester");

  const excludedEsMoneyAccounts = [stakedMoneyDistributorAddress, stakedMlpDistributorAddress];

  const nativeTokenSymbol = getConstant(chainId, "nativeTokenSymbol");
  const wrappedTokenSymbol = getConstant(chainId, "wrappedTokenSymbol");

  const walletTokens = [moneyAddress, esMoneyAddress, mlpAddress, stakedMoneyTrackerAddress];
  const depositTokens = [
    moneyAddress,
    esMoneyAddress,
    stakedMoneyTrackerAddress,
    bonusMoneyTrackerAddress,
    // extendedMoneyTrackerAddress,
    bnMoneyAddress,
    mlpAddress,
  ];
  const rewardTrackersForDepositBalances = [
    stakedMoneyTrackerAddress,
    stakedMoneyTrackerAddress,
    bonusMoneyTrackerAddress,
    feeMoneyTrackerAddress,
    feeMoneyTrackerAddress,
    feeMlpTrackerAddress,
  ];
  const rewardTrackersForStakingInfo = [
    stakedMoneyTrackerAddress,
    bonusMoneyTrackerAddress,
    feeMoneyTrackerAddress,
    stakedMlpTrackerAddress,
    feeMlpTrackerAddress,
    // extendedMoneyTrackerAddress,
  ];

  const { marketTokensData } = useMarketTokensData(chainId, { isDeposit: false });
  const {
    marketsTokensApyData,
    marketsTokensIncentiveAprData,
    glvTokensIncentiveAprData,
    marketsTokensLidoAprData,
    glvApyInfoData,
  } = useGmMarketsApy(chainId);
  const vestingData = useVestingData(account);
  // const govTokenAmount = useGovTokenAmount(chainId);
  // const govTokenDelegatesAddress = useGovTokenDelegates(chainId);
  // const { ensName: govTokenDelegatesEns } = useENS(govTokenDelegatesAddress);

  const { data: walletBalances, mutate: refetchBalances } = useSWR(
    [
      `StakeV2:walletBalances:${active}`,
      chainId,
      readerAddress,
      "getTokenBalancesWithSupplies",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(signer, ReaderV2, [walletTokens]),
    }
  );

  const { data: depositBalances } = useSWR(
    [
      `StakeV2:depositBalances:${active}`,
      chainId,
      rewardReaderAddress,
      "getDepositBalances",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(signer, RewardReader, [depositTokens, rewardTrackersForDepositBalances]),
    }
  );

  const { data: stakingInfo } = useSWR(
    [`StakeV2:stakingInfo:${active}`, chainId, rewardReaderAddress, "getStakingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(signer, RewardReader, [rewardTrackersForStakingInfo]),
    }
  );

  const { data: stakedMoneySupply } = useSWR(
    [`StakeV2:stakedMoneySupply:${active}`, chainId, moneyAddress, "balanceOf", stakedMoneyTrackerAddress],
    {
      fetcher: contractFetcher(signer, Token),
    }
  );

  const { data: aums } = useSWR([`StakeV2:getAums:${active}`, chainId, mlpManagerAddress, "getAums"], {
    fetcher: contractFetcher(signer, MlpManager),
  });

  const { data: nativeTokenPrice } = useSWR(
    [`StakeV2:nativeTokenPrice:${active}`, chainId, vaultAddress, "getMinPrice", nativeTokenAddress],
    {
      fetcher: contractFetcher(signer, Vault),
    }
  );

  const { data: esMoneySupply } = useSWR(
    [`StakeV2:esMoneySupply:${active}`, chainId, readerAddress, "getTokenSupply", esMoneyAddress],
    {
      fetcher: contractFetcher(signer, ReaderV2, [excludedEsMoneyAccounts]),
    }
  );

  const { data: sbfMoneyBalance } = useSWR(
    [`StakeV2:sbfMoneyBalance:${active}`, chainId, feeMoneyTrackerAddress, "balanceOf", account ?? PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const { moneyPrice, moneyPriceFromArbitrum, moneyPriceFromBsc } = useMoneyPrice(
    chainId,
    { arbitrum: chainId === ARBITRUM ? signer : undefined },
    active
  );

  let { total: totalMoneySupply } = useTotalMoneySupply();

  const stakedMONEYInfo = useTotalMoneyStaked();
  const { [BSC]: ronMoneyStaked, [ARBITRUM]: arbitrumMoneyStaked, total: totalMoneyStaked } = stakedMONEYInfo;

  // const moneySupplyUrl = getServerUrl(chainId, "/money_supply");
  // const { data: moneySupply } = useSWR([moneySupplyUrl], {
  //   fetcher: (args) => fetch(...args).then((res) => res.text()),
  // });

  const { data: moneySupply } = useSWR(
    [`StakeV2:moneySupply:${active}`, chainId, moneyAddress, "balanceOf", account ?? PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const isMoneyTransferEnabled = true;

  let esMoneySupplyUsd;
  if (esMoneySupply && moneyPrice) {
    esMoneySupplyUsd = bigMath.mulDiv(esMoneySupply, moneyPrice, expandDecimals(1, 18));
  }

  let aum;
  if (aums && aums.length > 0) {
    aum = (aums[0] + aums[1]) / 2n;
  }

  const { balanceData, supplyData } = useMemo(() => getBalanceAndSupplyData(walletBalances), [walletBalances]);
  const depositBalanceData = useMemo(() => getDepositBalanceData(depositBalances), [depositBalances]);
  const stakingData = useMemo(() => getStakingData(stakingInfo), [stakingInfo]);

  const userTotalGmInfo = useMemo(() => {
    if (!active) return;
    return getTotalGmInfo(marketTokensData);
  }, [marketTokensData, active]);

  const processedData = getProcessedData(
    balanceData,
    supplyData,
    depositBalanceData,
    stakingData,
    vestingData,
    aum,
    nativeTokenPrice,
    stakedMoneySupply,
    moneyPrice,
    moneySupply
  );

  const isAnyNativeTokenRewards =
    (processedData?.totalNativeTokenRewardsUsd ?? 0n) > 10n ** BigInt(USD_DECIMALS) / 100n;
  const isAnyFeeMoneyTrackerRewards = (processedData?.feeMoneyTrackerRewardsUsd ?? 0n) > 10n ** BigInt(USD_DECIMALS) / 100n;

  const reservedAmount =
    (processedData?.moneyInStakedMoney !== undefined &&
      processedData?.esMoneyInStakedMoney !== undefined &&
      sbfMoneyBalance !== undefined &&
      processedData?.moneyInStakedMoney + processedData?.esMoneyInStakedMoney - sbfMoneyBalance) ||
    0n;

  let totalRewardTokens;

  if (processedData && processedData.bonusMoneyInFeeMoney !== undefined) {
    totalRewardTokens = processedData.bonusMoneyInFeeMoney;
  }

  let totalRewardAndLpTokens = totalRewardTokens ?? 0n;
  if (processedData?.mlpBalance !== undefined) {
    totalRewardAndLpTokens = totalRewardAndLpTokens + processedData.mlpBalance;
  }
  if ((userTotalGmInfo?.balance ?? 0n) > 0) {
    totalRewardAndLpTokens = totalRewardAndLpTokens + (userTotalGmInfo?.balance ?? 0n);
  }

  let stakedMoneySupplyUsd;
  if (totalMoneyStaked !== 0n && moneyPrice) {
    stakedMoneySupplyUsd = bigMath.mulDiv(totalMoneyStaked, moneyPrice, expandDecimals(1, 18));
  }

  let totalSupplyUsd;
  if (totalMoneySupply !== undefined && totalMoneySupply !== 0n && moneyPrice) {
    totalSupplyUsd = bigMath.mulDiv(totalMoneySupply, moneyPrice, expandDecimals(1, 18));
  }

  const showStakeMoneyModal = useCallback(() => {
    if (!isMoneyTransferEnabled) {
      helperToast.error(t`MONEY transfers not yet enabled`);
      return;
    }

    setIsStakeMoneyModalVisible(true);
    setStakeMoneyValue("");
  }, [isMoneyTransferEnabled]);

  const showStakeEsMoneyModal = useCallback(() => {
    setIsStakeEsMoneyModalVisible(true);
    setStakeEsMoneyValue("");
  }, []);

  const showMoneyVesterDepositModal = () => {
    if (!vestingData) return;

    let remainingVestableAmount = vestingData.moneyVester.maxVestableAmount - vestingData.moneyVester.vestedAmount;
    if (processedData?.esMoneyBalance !== undefined && processedData?.esMoneyBalance < remainingVestableAmount) {
      remainingVestableAmount = processedData.esMoneyBalance;
    }

    setIsVesterDepositModalVisible(true);
    setVesterDepositTitle(t`MONEY Vault`);
    setVesterDepositStakeTokenLabel("staked MONEY + esMONEY");
    setVesterDepositMaxAmount(remainingVestableAmount);
    setVesterDepositBalance(processedData?.esMoneyBalance);
    setVesterDepositVestedAmount(vestingData.moneyVester.vestedAmount);
    setVesterDepositMaxVestableAmount(vestingData.moneyVester.maxVestableAmount);
    setVesterDepositAverageStakedAmount(vestingData.moneyVester.averageStakedAmount);
    setVesterDepositReserveAmount(reservedAmount);
    setVesterDepositMaxReserveAmount(totalRewardTokens);
    setVesterDepositValue("");
    setVesterDepositAddress(moneyVesterAddress);
  };

  const showMlpVesterDepositModal = () => {
    if (!vestingData) return;

    let remainingVestableAmount = vestingData.mlpVester.maxVestableAmount - vestingData.mlpVester.vestedAmount;
    if (processedData?.esMoneyBalance !== undefined && processedData?.esMoneyBalance < remainingVestableAmount) {
      remainingVestableAmount = processedData.esMoneyBalance;
    }

    setIsVesterDepositModalVisible(true);
    setVesterDepositTitle(t`MLP Vault`);
    setVesterDepositStakeTokenLabel("staked MLP");
    setVesterDepositMaxAmount(remainingVestableAmount);
    setVesterDepositBalance(processedData?.esMoneyBalance);
    setVesterDepositVestedAmount(vestingData.mlpVester.vestedAmount);
    setVesterDepositMaxVestableAmount(vestingData.mlpVester.maxVestableAmount);
    setVesterDepositAverageStakedAmount(vestingData.mlpVester.averageStakedAmount);
    setVesterDepositReserveAmount(vestingData.mlpVester.pairAmount);
    setVesterDepositMaxReserveAmount(processedData?.mlpBalance);
    setVesterDepositValue("");
    setVesterDepositAddress(mlpVesterAddress);
  };

  const showMoneyVesterWithdrawModal = () => {
    if (!vestingData || vestingData.moneyVesterVestedAmount === undefined || vestingData.moneyVesterVestedAmount === 0n) {
      helperToast.error(t`You have not deposited any tokens for vesting.`);
      return;
    }

    setIsVesterWithdrawModalVisible(true);
    setVesterWithdrawTitle(t`Withdraw from MONEY Vault`);
    setVesterWithdrawAddress(moneyVesterAddress);
  };

  const showMlpVesterWithdrawModal = () => {
    if (!vestingData || vestingData.mlpVesterVestedAmount === undefined || vestingData.mlpVesterVestedAmount === 0n) {
      helperToast.error(t`You have not deposited any tokens for vesting.`);
      return;
    }

    setIsVesterWithdrawModalVisible(true);
    setVesterWithdrawTitle(t`Withdraw from MLP Vault`);
    setVesterWithdrawAddress(mlpVesterAddress);
  };

  const showUnstakeMoneyModal = () => {
    if (!isMoneyTransferEnabled) {
      helperToast.error(t`MONEY transfers not yet enabled`);
      return;
    }
    setIsUnstakeModalVisible(true);
    setUnstakeModalTitle(t`Unstake MONEY`);
    let maxAmount = processedData?.moneyInStakedMoney;

    if (maxAmount !== undefined) {
      maxAmount = bigMath.min(maxAmount, sbfMoneyBalance);
    }

    setUnstakeModalMaxAmount(maxAmount);
    setUnstakeValue("");
    setUnstakingTokenSymbol("MONEY");
    setUnstakeMethodName("unstakeMoney");
  };

  const showUnstakeEsMoneyModal = () => {
    setIsUnstakeModalVisible(true);
    setUnstakeModalTitle(t`Unstake esMONEY`);
    let maxAmount = processedData?.esMoneyInStakedMoney;

    if (maxAmount !== undefined) {
      maxAmount = bigMath.min(maxAmount, sbfMoneyBalance);
    }

    setUnstakeModalMaxAmount(maxAmount);
    setUnstakeValue("");
    setUnstakingTokenSymbol("esMONEY");
    setUnstakeMethodName("unstakeEsMoney");
  };

  function showAffiliateVesterDepositModal() {
    if (!vestingData?.affiliateVester) {
      helperToast.error(t`Unsupported network`);
      return;
    }

    let remainingVestableAmount =
      vestingData.affiliateVester.maxVestableAmount - vestingData.affiliateVester.vestedAmount;
    if (processedData?.esMoneyBalance !== undefined && processedData.esMoneyBalance < remainingVestableAmount) {
      remainingVestableAmount = processedData.esMoneyBalance;
    }

    setIsVesterDepositModalVisible(true);
    setVesterDepositTitle(t`Affiliate Vault`);

    setVesterDepositMaxAmount(remainingVestableAmount);
    setVesterDepositBalance(processedData?.esMoneyBalance);
    setVesterDepositVestedAmount(vestingData?.affiliateVester.vestedAmount);
    setVesterDepositMaxVestableAmount(vestingData?.affiliateVester.maxVestableAmount);
    setVesterDepositAverageStakedAmount(vestingData?.affiliateVester.averageStakedAmount);

    setVesterDepositReserveAmount(undefined);
    setVesterDepositValue("");

    setVesterDepositAddress(affiliateVesterAddress);
  }

  function showAffiliateVesterWithdrawModal() {
    if (vestingData?.affiliateVesterVestedAmount === undefined || vestingData.affiliateVesterVestedAmount <= 0) {
      helperToast.error(t`You have not deposited any tokens for vesting.`);
      return;
    }

    setIsAffiliateVesterWithdrawModalVisible(true);
  }

  function showAffiliateVesterClaimModal() {
    if (vestingData?.affiliateVesterClaimable === undefined || vestingData?.affiliateVesterClaimable <= 0) {
      helperToast.error(t`You have no MONEY tokens to claim.`);
      return;
    }
    setIsAffiliateClaimModalVisible(true);
  }

  const moneyAvgAprText = useMemo(() => {
    return `${formatAmount(processedData?.moneyAprTotal, 2, 2, true)}%`;
  }, [processedData?.moneyAprTotal]);

  let earnMsg;
  if (totalRewardAndLpTokens && totalRewardAndLpTokens > 0) {
    let moneyAmountStr;
    if (processedData?.moneyInStakedMoney && processedData.moneyInStakedMoney > 0) {
      moneyAmountStr = formatAmount(processedData.moneyInStakedMoney, 18, 2, true) + " MONEY";
    }
    let esMoneyAmountStr;
    if (processedData?.esMoneyInStakedMoney && processedData.esMoneyInStakedMoney > 0) {
      esMoneyAmountStr = formatAmount(processedData.esMoneyInStakedMoney, 18, 2, true) + " esMONEY";
    }
    let mlpStr;
    if (processedData?.mlpBalance && processedData.mlpBalance > 0) {
      mlpStr = formatAmount(processedData.mlpBalance, 18, 2, true) + " MLP";
    }
    let gmStr;
    if (userTotalGmInfo?.balance && userTotalGmInfo.balance > 0) {
      gmStr = formatAmount(userTotalGmInfo.balance, 18, 2, true) + " GM";
    }
    const amountStr = [moneyAmountStr, esMoneyAmountStr, gmStr, mlpStr].filter((s) => s).join(", ");
    earnMsg = (
      <div>
        <Trans>
          You are earning rewards with {formatAmount(totalRewardAndLpTokens, 18, 2, true)} tokens.
          <br />
          Tokens: {amountStr}.
        </Trans>
      </div>
    );
  }

  const stakedEntries = useMemo(
    () => ({
      // "Staked on Ronin": arbitrumMoneyStaked,
      "Staked on Bsc": ronMoneyStaked,
    }),
    // [arbitrumMoneyStaked, ronMoneyStaked]
    [ronMoneyStaked]
  );

  const moneyMarketAddress = useMemo(() => {
    if (chainId === ARBITRUM) {
      return Object.values(MARKETS[ARBITRUM]).find((m) => m.indexTokenAddress === moneyAddress)?.marketTokenAddress;
    }
    return undefined;
  }, [chainId, moneyAddress]);

  const moneyMarketApyDataText = useMemo(() => {
    if (!moneyMarketAddress || chainId !== ARBITRUM) return;

    const moneyApy =
      (marketsTokensApyData?.[moneyMarketAddress] ?? 0n) + (marketsTokensIncentiveAprData?.[moneyMarketAddress] ?? 0n);

    return `${formatAmount(moneyApy, 28, 2, true)}%`;
  }, [marketsTokensApyData, marketsTokensIncentiveAprData, moneyMarketAddress, chainId]);

  const hideToasts = useCallback(() => toast.dismiss(), []);
  const handleStakeMoney = useCallback(async () => {
    hideToasts();
    showStakeMoneyModal();
  }, [showStakeMoneyModal, hideToasts]);

  return (
    <div className="default-container page-layout">
      <StakeModal
        isVisible={isStakeMoneyModalVisible}
        setIsVisible={setIsStakeMoneyModalVisible}
        chainId={chainId}
        title={t`Stake MONEY`}
        maxAmount={processedData?.moneyBalance}
        value={stakeMoneyValue}
        setValue={setStakeMoneyValue}
        signer={signer}
        stakingTokenSymbol="MONEY"
        stakingTokenAddress={moneyAddress}
        farmAddress={stakedMoneyTrackerAddress}
        rewardRouterAddress={rewardRouterAddress}
        stakeMethodName="stakeMoney"
        setPendingTxns={setPendingTxns}
        processedData={processedData}
      />
      <StakeModal
        isVisible={isStakeEsMoneyModalVisible}
        setIsVisible={setIsStakeEsMoneyModalVisible}
        chainId={chainId}
        title={t`Stake esMONEY`}
        maxAmount={processedData?.esMoneyBalance}
        value={stakeEsMoneyValue}
        setValue={setStakeEsMoneyValue}
        signer={signer}
        stakingTokenSymbol="esMONEY"
        stakingTokenAddress={esMoneyAddress}
        farmAddress={ZeroAddress}
        rewardRouterAddress={rewardRouterAddress}
        stakeMethodName="stakeEsMoney"
        setPendingTxns={setPendingTxns}
        processedData={processedData}
      />
      <UnstakeModal
        setPendingTxns={setPendingTxns}
        isVisible={isUnstakeModalVisible}
        setIsVisible={setIsUnstakeModalVisible}
        chainId={chainId}
        title={unstakeModalTitle}
        maxAmount={unstakeModalMaxAmount}
        reservedAmount={reservedAmount}
        value={unstakeValue}
        setValue={setUnstakeValue}
        signer={signer}
        unstakingTokenSymbol={unstakingTokenSymbol}
        rewardRouterAddress={rewardRouterAddress}
        unstakeMethodName={unstakeMethodName}
        processedData={processedData}
      />
      <VesterDepositModal
        isVisible={isVesterDepositModalVisible}
        setIsVisible={setIsVesterDepositModalVisible}
        chainId={chainId}
        title={vesterDepositTitle}
        stakeTokenLabel={vesterDepositStakeTokenLabel}
        maxAmount={vesterDepositMaxAmount}
        balance={vesterDepositBalance}
        vestedAmount={vesterDepositVestedAmount}
        averageStakedAmount={
          typeof vesterDepositAverageStakedAmount === "string"
            ? vesterDepositAverageStakedAmount === ""
              ? undefined
              : BigInt(vesterDepositAverageStakedAmount)
            : vesterDepositAverageStakedAmount
        }
        maxVestableAmount={vesterDepositMaxVestableAmount}
        reserveAmount={vesterDepositReserveAmount}
        maxReserveAmount={vesterDepositMaxReserveAmount}
        value={vesterDepositValue}
        setValue={setVesterDepositValue}
        signer={signer}
        vesterAddress={vesterDepositAddress}
        setPendingTxns={setPendingTxns}
      />
      <VesterWithdrawModal
        isVisible={isVesterWithdrawModalVisible}
        setIsVisible={setIsVesterWithdrawModalVisible}
        vesterAddress={vesterWithdrawAddress}
        chainId={chainId}
        title={vesterWithdrawTitle}
        signer={signer}
        setPendingTxns={setPendingTxns}
      />
      <AffiliateVesterWithdrawModal
        isVisible={isAffiliateVesterWithdrawModalVisible}
        setIsVisible={setIsAffiliateVesterWithdrawModalVisible}
        chainId={chainId}
        signer={signer}
        setPendingTxns={setPendingTxns}
      />
      <ClaimModal
        setPendingTxns={setPendingTxns}
        isVisible={isCompoundModalVisible}
        setIsVisible={setIsCompoundModalVisible}
        rewardRouterAddress={rewardRouterAddress}
        totalMoneyRewards={processedData?.totalMoneyRewards}
        wrappedTokenSymbol={wrappedTokenSymbol}
        nativeTokenSymbol={nativeTokenSymbol}
        signer={signer}
        chainId={chainId}
        isNativeTokenToClaim={isAnyNativeTokenRewards}
        onClaimSuccess={refetchBalances}
        moneyUsageOptionsMsg={
          <ul className="list-disc">
            <li className="!pb-0">
              <Trans>
                <Link className="link-underline" to="#" onClick={handleStakeMoney}>
                  Stake MONEY
                </Link>{" "}
                and earn {moneyAvgAprText} APR
              </Trans>
            </li>
            {chainId === ARBITRUM && (
              <>
                <li className="!pb-0">
                  <Trans>
                    <Link
                      className="link-underline"
                      to={`/pools/?market=${moneyMarketAddress}&operation=buy&scroll=1`}
                      onClick={hideToasts}
                    >
                      Provide liquidity
                    </Link>{" "}
                    and earn {moneyMarketApyDataText} APY
                  </Trans>
                </li>
                <li className="!pb-0">
                  <Trans>
                    <Link
                      className="link-underline"
                      to={`/trade/long/?mode=market&from=money&market=money`}
                      onClick={hideToasts}
                    >
                      Trade MONEY
                    </Link>
                  </Trans>
                </li>
              </>
            )}
          </ul>
        }
      />
      <AffiliateClaimModal
        signer={signer}
        chainId={chainId}
        setPendingTxns={setPendingTxns}
        isVisible={isAffiliateClaimModalVisible}
        setIsVisible={setIsAffiliateClaimModalVisible}
        totalVesterRewards={vestingData?.affiliateVesterClaimable ?? BN_ZERO}
      />

      <PageTitle
        isTop
        title={t`Earn`}
        qa="earn-page"
        subtitle={
          <div>
            <Trans>
              Stake <ExternalLink href="https://moneyx.nuclino-site.com">MONEY</ExternalLink> and buy{" "}
              {/* <ExternalLink href="https://moneyx.nuclino-site.com">GM</ExternalLink> or{" "} */}
              <ExternalLink href="https://moneyx.nuclino-site.com">MLP</ExternalLink> to earn rewards.
            </Trans>
            {earnMsg && <div className="Page-descriptionж">{earnMsg}</div>}
            {incentivesMessage}
          </div>
        }
      />
      <div className="StakeV2-content">
        <div className="StakeV2-cards">
          <div className="App-card StakeV2-money-card">
            <div className="App-card-title">
              <div className="inline-flex items-center">
                <img className="mr-5 h-20" alt="MONEY" src={icons?.money} height={20} />
                {t`MONEY & Voting Power`}
              </div>
            </div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">
                  <Trans>Price</Trans>
                </div>
                <div>
                  {!moneyPrice && "..."}
                  {moneyPrice && (
                    <Tooltip
                      position="bottom-end"
                      className="whitespace-nowrap"
                      handle={"$" + formatAmount(moneyPrice, USD_DECIMALS, 2, true)}
                      renderContent={() => (
                        <>
                          <StatsTooltipRow
                            label={t`Price on Bsc`}
                            value={formatAmount(moneyPriceFromBsc, USD_DECIMALS, 2, true)}
                          />
                          {/* <StatsTooltipRow
                            label={t`Price on Ronin`}
                            value={formatAmount(moneyPriceFromArbitrum, USD_DECIMALS, 2, true)}
                          /> */}
                        </>
                      )}
                    />
                  )}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Wallet</Trans>
                </div>
                <div>
                  {formatKeyAmount(processedData, "moneyBalance", 18, 2, true)} MONEY ($
                  {formatKeyAmount(processedData, "moneyBalanceUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Staked</Trans>
                </div>
                <div>
                  {formatKeyAmount(processedData, "moneyInStakedMoney", 18, 2, true)} MONEY ($
                  {formatKeyAmount(processedData, "moneyInStakedMoneyUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              {chainId === ARBITRUM && (
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Voting Power</Trans>
                  </div>
                  <div>
                    {/* {govTokenAmount !== undefined ? (
                      <Tooltip
                        position="bottom-end"
                        className="nowrap"
                        handle={`${formatAmount(govTokenAmount, 18, 2, true)} MONEY DAO`}
                        renderContent={() => (
                          <>
                            {govTokenDelegatesAddress === NATIVE_TOKEN_ADDRESS && govTokenAmount > 0 ? (
                              <AlertInfo
                                type="warning"
                                className={cx("DelegateMONEYAlertInfo")}
                                textColor="text-yellow-500"
                              >
                                <Trans>
                                  <ExternalLink href={MONEY_DAO_LINKS.VOTING_POWER} className="display-inline">
                                    Delegate your undelegated {formatAmount(govTokenAmount, 18, 2, true)} MONEY DAO
                                  </ExternalLink>{" "}
                                  voting power.
                                </Trans>
                              </AlertInfo>
                            ) : null}
                            <StatsTooltipRow
                              label={t`Delegated to`}
                              value={
                                !govTokenDelegatesAddress || govTokenDelegatesAddress === NATIVE_TOKEN_ADDRESS ? (
                                  <Trans>No delegate found</Trans>
                                ) : (
                                  <ExternalLink href={getMoneyDAODelegateLink(govTokenDelegatesAddress)}>
                                    {govTokenDelegatesAddress === account
                                      ? t`Myself`
                                      : govTokenDelegatesEns
                                        ? shortenAddressOrEns(govTokenDelegatesEns, 25)
                                        : shortenAddressOrEns(govTokenDelegatesAddress, 13)}
                                  </ExternalLink>
                                )
                              }
                              showDollar={false}
                            />
                            <br />
                            <ExternalLink href={MONEY_DAO_LINKS.DELEGATES}>Explore the list of delegates</ExternalLink>.
                          </>
                        )}
                      />
                    ) : (
                      "..."
                    )} */}
                  </div>
                </div>
              )}
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>APR</Trans>
                </div>
                <div>
                  <Tooltip
                    handle={moneyAvgAprText}
                    position="bottom-end"
                    renderContent={() => (
                      <MONEYAprTooltip processedData={processedData} nativeTokenSymbol={nativeTokenSymbol} />
                    )}
                  />
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Rewards</Trans>
                </div>
                <div>
                  <Tooltip
                    handle={`$${formatKeyAmount(processedData, "totalStakingRewardsUsd", USD_DECIMALS, 2, true)}`}
                    position="bottom-end"
                    renderContent={() => {
                      return (
                        <>
                          <StatsTooltipRow
                            label={t`MONEY`}
                            value={`${formatKeyAmount(
                              processedData,
                              "extendedMoneyTrackerRewards",
                              18,
                              4
                            )} ($${formatKeyAmount(processedData, "extendedMoneyTrackerRewardsUsd", USD_DECIMALS, 2, true)})`}
                            showDollar={false}
                          />
                          <StatsTooltipRow
                            label="Escrowed MONEY"
                            value={`${formatKeyAmount(
                              processedData,
                              "stakedMoneyTrackerRewards",
                              18,
                              4
                            )} ($${formatKeyAmount(
                              processedData,
                              "stakedMoneyTrackerRewardsUsd",
                              USD_DECIMALS,
                              2,
                              true
                            )})`}
                            showDollar={false}
                          />
                          {isAnyFeeMoneyTrackerRewards && (
                            <StatsTooltipRow
                              label={`${nativeTokenSymbol} (${wrappedTokenSymbol})`}
                              value={`${formatKeyAmount(
                                processedData,
                                "feeMoneyTrackerRewards",
                                18,
                                4
                              )} ($${formatKeyAmount(processedData, "feeMoneyTrackerRewardsUsd", USD_DECIMALS, 2, true)})`}
                              showDollar={false}
                            />
                          )}
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Total Staked</Trans>
                </div>
                <div>
                  {totalMoneyStaked === undefined && "..."}
                  {(totalMoneyStaked !== undefined && (
                    <Tooltip
                      position="bottom-end"
                      className="whitespace-nowrap"
                      handle={
                        formatAmount(totalMoneyStaked, 18, 0, true) +
                        " MONEY" +
                        ` ($${formatAmount(stakedMoneySupplyUsd, USD_DECIMALS, 0, true)})`
                      }
                      renderContent={() => (
                        <ChainsStatsTooltipRow
                          showDollar={false}
                          decimalsForConversion={18}
                          symbol="MONEY"
                          entries={stakedEntries}
                        />
                      )}
                    />
                  )) ||
                    null}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Total Supply</Trans>
                </div>
                {totalMoneySupply === undefined && "..."}
                {(totalMoneySupply !== undefined && (
                  <div>
                    {formatAmount(totalMoneySupply, 18, 0, true)} MONEY ($
                    {formatAmount(totalSupplyUsd, USD_DECIMALS, 0, true)})
                  </div>
                )) ||
                  null}
              </div>
              <div className="App-card-divider" />
              <div className="App-card-buttons m-0">
                <Button variant="secondary" to="/buy_money">
                  <Trans>Buy MONEY</Trans>
                </Button>
                {active && (
                  <Button variant="secondary" onClick={() => showStakeMoneyModal()}>
                    <Trans>Stake</Trans>
                  </Button>
                )}
                {active && (
                  <Button variant="secondary" onClick={() => showUnstakeMoneyModal()}>
                    <Trans>Unstake</Trans>
                  </Button>
                )}
                {active && chainId === ARBITRUM && (
                  <Button variant="secondary" to={MONEY_DAO_LINKS.VOTING_POWER} newTab>
                    <Trans>Delegate</Trans>
                  </Button>
                )}
                {active && (
                  <Button variant="secondary" to="/begin_account_transfer">
                    <Trans>Transfer Account</Trans>
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="App-card primary StakeV2-total-rewards-card">
            <div className="App-card-title">
              <Trans>Total Rewards</Trans>
            </div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">MONEY</div>
                <Tooltip
                  handle={
                    <div>
                      {formatKeyAmount(processedData, "totalMoneyRewards", 18, 4, true)} ($
                      {formatKeyAmount(processedData, "totalMoneyRewardsUsd", USD_DECIMALS, 2, true)})
                    </div>
                  }
                  position="bottom-end"
                  content={
                    <>
                      <StatsTooltipRow
                        label={
                          <>
                            {t`MONEY Staked Rewards`}:
                            <div className="mx-4 inline" />
                          </>
                        }
                        showDollar={false}
                        value={
                          <>
                            {formatKeyAmount(processedData, "extendedMoneyTrackerRewards", 18, 4, true)} ($
                            {formatKeyAmount(processedData, "extendedMoneyTrackerRewardsUsd", USD_DECIMALS, 2, true)})
                          </>
                        }
                      />
                      <StatsTooltipRow
                        label={
                          <>
                            {t`Vested Claimable MONEY`}:
                            <div className="mx-4 inline" />
                          </>
                        }
                        showDollar={false}
                        value={
                          <>
                            {formatKeyAmount(processedData, "totalVesterRewards", 18, 4, true)} ($
                            {formatKeyAmount(processedData, "totalVesterRewardsUsd", USD_DECIMALS, 2, true)})
                          </>
                        }
                      />
                    </>
                  }
                />
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Escrowed MONEY</Trans>
                </div>
                <div>
                  {formatKeyAmount(processedData, "totalEsMoneyRewards", 18, 4, true)} ($
                  {formatKeyAmount(processedData, "totalEsMoneyRewardsUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              {isAnyNativeTokenRewards ? (
                <div className="App-card-row">
                  <div className="label">
                    {nativeTokenSymbol} ({wrappedTokenSymbol})
                  </div>
                  <div>
                    {formatKeyAmount(processedData, "totalNativeTokenRewards", 18, 4, true)} ($
                    {formatKeyAmount(processedData, "totalNativeTokenRewardsUsd", USD_DECIMALS, 2, true)})
                  </div>
                </div>
              ) : null}
              <div className="App-card-row">
                <div className="label">
                  <Trans>Total</Trans>
                </div>
                <div>${formatKeyAmount(processedData, "totalRewardsUsd", USD_DECIMALS, 2, true)}</div>
              </div>
              <div className="App-card-footer">
                <div className="App-card-divider"></div>
                <div className="App-card-buttons m-0">
                  {active && (
                    <Button variant="secondary" onClick={() => setIsCompoundModalVisible(true)}>
                      <Trans>Claim</Trans>
                    </Button>
                  )}
                  {!active && (
                    <Button variant="secondary" onClick={openConnectModal}>
                      <Trans>Connect Wallet</Trans>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="App-card App-card-space-between">
            <div>
              <div className="App-card-title">
                <div className="inline-flex items-center">
                  <img className="mr-5 h-20" alt="MLP" src={icons?.mlp} height={20} />
                  MLP
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-content">
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Price</Trans>
                  </div>
                  <div>${formatKeyAmount(processedData, "mlpPrice", USD_DECIMALS, MLP_PRICE_DECIMALS, true)}</div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Wallet</Trans>
                  </div>
                  <div>
                    {formatKeyAmount(processedData, "mlpBalance", MLP_DECIMALS, 2, true)} MLP ($
                    {formatKeyAmount(processedData, "mlpBalanceUsd", USD_DECIMALS, 2, true)})
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Staked</Trans>
                  </div>
                  <div>
                    {formatKeyAmount(processedData, "mlpBalance", MLP_DECIMALS, 2, true)} MLP ($
                    {formatKeyAmount(processedData, "mlpBalanceUsd", USD_DECIMALS, 2, true)})
                  </div>
                </div>
                <div className="App-card-divider"></div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>APR</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(processedData, "mlpAprTotal", 2, 2, true)}%`}
                      position="bottom-end"
                      renderContent={() => {
                        return (
                          <>
                            <StatsTooltipRow
                              label={`${nativeTokenSymbol} (${wrappedTokenSymbol}) APR`}
                              value={`${formatKeyAmount(processedData, "mlpAprForNativeToken", 2, 2, true)}%`}
                              showDollar={false}
                            />

                            {processedData?.mlpAprForEsMoney && processedData.mlpAprForEsMoney > 0 && (
                              <StatsTooltipRow
                                label="Escrowed MONEY APR"
                                value={`${formatKeyAmount(processedData, "mlpAprForEsMoney", 2, 2, true)}%`}
                                showDollar={false}
                              />
                            )}

                            <br />

                            <Trans>
                              APRs are updated weekly on Wednesday and will depend on the fees collected for the week.{" "}
                              <br />
                              <br />
                              Historical MLP APRs can be checked in this{" "}
                              <ExternalLink href="https://moneyxpro.com/stats">
                                community dashboard
                              </ExternalLink>
                              .
                            </Trans>
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Rewards</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={`$${formatKeyAmount(processedData, "totalMlpRewardsUsd", USD_DECIMALS, 2, true)}`}
                      position="bottom-end"
                      renderContent={() => {
                        return (
                          <>
                            <StatsTooltipRow
                              label={`${nativeTokenSymbol} (${wrappedTokenSymbol})`}
                              value={`${formatKeyAmount(
                                processedData,
                                "feeMlpTrackerRewards",
                                18,
                                4
                              )} ($${formatKeyAmount(
                                processedData,
                                "feeMlpTrackerRewardsUsd",
                                USD_DECIMALS,
                                2,
                                true
                              )})`}
                              showDollar={false}
                            />
                            <StatsTooltipRow
                              label="Escrowed MONEY"
                              value={`${formatKeyAmount(
                                processedData,
                                "stakedMlpTrackerRewards",
                                18,
                                4
                              )} ($${formatKeyAmount(
                                processedData,
                                "stakedMlpTrackerRewardsUsd",
                                USD_DECIMALS,
                                2,
                                true
                              )})`}
                              showDollar={false}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-divider" />
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Total Staked</Trans>
                  </div>
                  <div>
                    {formatKeyAmount(processedData, "mlpSupply", 18, 2, true)} MLP ($
                    {formatKeyAmount(processedData, "mlpSupplyUsd", USD_DECIMALS, 2, true)})
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Total Supply</Trans>
                  </div>
                  <div>
                    {formatKeyAmount(processedData, "mlpSupply", 18, 2, true)} MLP ($
                    {formatKeyAmount(processedData, "mlpSupplyUsd", USD_DECIMALS, 2, true)})
                  </div>
                </div>

                <div />
              </div>
            </div>
            <div>
              <div className="App-card-divider" />
              <div className="App-card-buttons mlp-buttons m-0">
                <Button variant="secondary" to="/buy_mlp">
                  <Trans>Buy MLP</Trans>
                </Button>
                <Button variant="secondary" to="/buy_mlp#redeem">
                  <Trans>Sell MLP</Trans>
                </Button>
                {hasInsurance && (
                  <Button
                    variant="secondary"
                    to="https://app.insurace.io/Insurance/Cart?id=124&referrer=545066382753150189457177837072918687520318754040"
                  >
                    <Trans>Purchase Insurance</Trans>
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">
              <div className="inline-flex items-center">
                <img className="mr-5 h-20" alt="MLP" src={icons?.esmoney} height={20} />
                <span>
                  <Trans>Escrowed MONEY</Trans>
                </span>
              </div>
            </div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">
                  <Trans>Price</Trans>
                </div>
                <div>${formatAmount(moneyPrice, USD_DECIMALS, 2, true)}</div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Wallet</Trans>
                </div>
                <div>
                  {formatKeyAmount(processedData, "esMoneyBalance", 18, 2, true)} esMONEY ($
                  {formatKeyAmount(processedData, "esMoneyBalanceUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Staked</Trans>
                </div>
                <div>
                  {formatKeyAmount(processedData, "esMoneyInStakedMoney", 18, 2, true)} esMONEY ($
                  {formatKeyAmount(processedData, "esMoneyInStakedMoneyUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>APR</Trans>
                </div>
                <div>
                  <Tooltip
                    handle={moneyAvgAprText}
                    position="bottom-end"
                    renderContent={() => (
                      <MONEYAprTooltip processedData={processedData!} nativeTokenSymbol={nativeTokenSymbol} />
                    )}
                  />
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Total Staked</Trans>
                </div>
                <div>
                  {formatKeyAmount(processedData, "stakedEsMoneySupply", 18, 0, true)} esMONEY ($
                  {formatKeyAmount(processedData, "stakedEsMoneySupplyUsd", USD_DECIMALS, 0, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">
                  <Trans>Total Supply</Trans>
                </div>
                <div>
                  {formatAmount(esMoneySupply, 18, 0, true)} esMONEY (${formatAmount(esMoneySupplyUsd, USD_DECIMALS, 0, true)}
                  )
                </div>
              </div>
              <div className="App-card-footer">
                <div className="App-card-divider"></div>
                <div className="App-card-buttons m-0">
                  <br />
                  {active && (
                    <Button variant="secondary" onClick={() => showStakeEsMoneyModal()}>
                      <Trans>Stake</Trans>
                    </Button>
                  )}
                  {active && (
                    <Button variant="secondary" onClick={() => showUnstakeEsMoneyModal()}>
                      <Trans>Unstake</Trans>
                    </Button>
                  )}
                  {!active && (
                    <Button variant="secondary" onClick={openConnectModal}>
                      <Trans> Connect Wallet</Trans>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {getIsSyntheticsSupported(chainId) && (
        <div className="StakeV2-section">
          <GmList
            marketsTokensApyData={marketsTokensApyData}
            marketsTokensIncentiveAprData={marketsTokensIncentiveAprData}
            glvTokensIncentiveAprData={glvTokensIncentiveAprData}
            marketsTokensLidoAprData={marketsTokensLidoAprData}
            glvTokensApyData={glvApyInfoData}
            isDeposit={false}
            shouldScrollToTop
          />
        </div>
      )}

      <div>
        <PageTitle
          title={t`Vest`}
          subtitle={
            <Trans>
              Convert esMONEY tokens to MONEY tokens.
              <br />
              Please read the{" "}
              <ExternalLink href="https://moneyx.nuclino-site.com">
                vesting details
              </ExternalLink>{" "}
              before using the vaults.
            </Trans>
          }
        />
        <div>
          <div className="StakeV2-cards">
            <div className="App-card StakeV2-money-card">
              <div className="App-card-title">
                <div className="inline-flex items-center">
                  <img className="mr-5 h-20" alt="MONEY" src={icons?.money} height={20} />
                  <Trans>MONEY Vault</Trans>
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-content">
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Staked Tokens</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={formatAmount(totalRewardTokens, 18, 2, true)}
                      position="bottom-end"
                      renderContent={() => {
                        return (
                          <>
                            <StatsTooltipRow
                              showDollar={false}
                              label="MONEY"
                              value={formatAmount(processedData?.moneyInStakedMoney, 18, 2, true)}
                            />

                            <StatsTooltipRow
                              showDollar={false}
                              label="esMONEY"
                              value={formatAmount(processedData?.esMoneyInStakedMoney, 18, 2, true)}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Reserved for Vesting</Trans>
                  </div>
                  <div>
                    {formatAmount(reservedAmount, 18, 2, true)} / {formatAmount(totalRewardTokens, 18, 2, true)}
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Vesting Status</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "moneyVesterClaimSum", 18, 4, true)} / ${formatKeyAmount(
                        vestingData,
                        "moneyVesterVestedAmount",
                        18,
                        4,
                        true
                      )}`}
                      position="bottom-end"
                      renderContent={() => {
                        return (
                          <div>
                            <Trans>
                              {formatKeyAmount(vestingData, "moneyVesterClaimSum", 18, 4, true)} tokens have been
                              converted to MONEY from the{" "}
                              {formatKeyAmount(vestingData, "moneyVesterVestedAmount", 18, 4, true)} esMONEY deposited for
                              vesting.
                            </Trans>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Claimable</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "moneyVesterClaimable", 18, 4, true)} MONEY`}
                      position="bottom-end"
                      renderContent={() => (
                        <Trans>
                          {formatKeyAmount(vestingData, "moneyVesterClaimable", 18, 4, true)} MONEY tokens can be claimed,
                          use the options under the Total Rewards section to claim them.
                        </Trans>
                      )}
                    />
                  </div>
                </div>
                <div className="App-card-divider"></div>
                <div className="App-card-buttons m-0">
                  {!active && (
                    <Button variant="secondary" onClick={openConnectModal}>
                      <Trans>Connect Wallet</Trans>
                    </Button>
                  )}
                  {active && (
                    <Button variant="secondary" onClick={() => showMoneyVesterDepositModal()}>
                      <Trans>Deposit</Trans>
                    </Button>
                  )}
                  {active && (
                    <Button variant="secondary" onClick={() => showMoneyVesterWithdrawModal()}>
                      <Trans>Withdraw</Trans>
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="App-card StakeV2-money-card">
              <div className="App-card-title">
                <div className="inline-flex items-center">
                  <img className="mr-5 h-20" alt="MLP" src={icons?.mlp} height={20} />
                  <Trans>MLP Vault</Trans>
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-content">
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Staked Tokens</Trans>
                  </div>
                  <div>{formatAmount(processedData?.mlpBalance, 18, 2, true)} MLP</div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Reserved for Vesting</Trans>
                  </div>
                  <div>
                    {formatKeyAmount(vestingData, "mlpVesterPairAmount", 18, 2, true)} /{" "}
                    {formatAmount(processedData?.mlpBalance, 18, 2, true)}
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Vesting Status</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "mlpVesterClaimSum", 18, 4, true)} / ${formatKeyAmount(
                        vestingData,
                        "mlpVesterVestedAmount",
                        18,
                        4,
                        true
                      )}`}
                      position="bottom-end"
                      renderContent={() => {
                        return (
                          <div>
                            <Trans>
                              {formatKeyAmount(vestingData, "mlpVesterClaimSum", 18, 4, true)} tokens have been
                              converted to MONEY from the{" "}
                              {formatKeyAmount(vestingData, "mlpVesterVestedAmount", 18, 4, true)} esMONEY deposited for
                              vesting.
                            </Trans>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">
                    <Trans>Claimable</Trans>
                  </div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "mlpVesterClaimable", 18, 4, true)} MONEY`}
                      position="bottom-end"
                      renderContent={() => (
                        <Trans>
                          {formatKeyAmount(vestingData, "mlpVesterClaimable", 18, 4, true)} MONEY tokens can be claimed,
                          use the options under the Total Rewards section to claim them.
                        </Trans>
                      )}
                    />
                  </div>
                </div>
                <div className="App-card-divider"></div>
                <div className="App-card-buttons m-0">
                  {!active && (
                    <Button variant="secondary" onClick={openConnectModal}>
                      <Trans>Connect Wallet</Trans>
                    </Button>
                  )}
                  {active && (
                    <Button variant="secondary" onClick={() => showMlpVesterDepositModal()}>
                      <Trans>Deposit</Trans>
                    </Button>
                  )}
                  {active && (
                    <Button variant="secondary" onClick={() => showMlpVesterWithdrawModal()}>
                      <Trans>Withdraw</Trans>
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {(vestingData?.affiliateVesterMaxVestableAmount && vestingData?.affiliateVesterMaxVestableAmount > 0 && (
              <div className="App-card StakeV2-money-card">
                <div className="App-card-title">
                  <div className="inline-flex items-center">
                    <img className="mr-5 h-20" alt="MLP" src={icons?.money} height={20} />
                    <Trans>Affiliate Vault</Trans>
                  </div>
                </div>
                <div className="App-card-divider" />
                <div className="App-card-content">
                  <div className="App-card-row">
                    <div className="label">
                      <Trans>Vesting Status</Trans>
                    </div>
                    <div>
                      <Tooltip
                        handle={`${formatKeyAmount(
                          vestingData,
                          "affiliateVesterClaimSum",
                          18,
                          4,
                          true
                        )} / ${formatKeyAmount(vestingData, "affiliateVesterVestedAmount", 18, 4, true)}`}
                        position="bottom-end"
                        renderContent={() => {
                          return (
                            <div>
                              <Trans>
                                {formatKeyAmount(vestingData, "affiliateVesterClaimSum", 18, 4, true)} tokens have been
                                converted to MONEY from the{" "}
                                {formatKeyAmount(vestingData, "affiliateVesterVestedAmount", 18, 4, true)} esMONEY
                                deposited for vesting.
                              </Trans>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="App-card-row">
                    <div className="label">
                      <Trans>Claimable</Trans>
                    </div>
                    <div>{formatKeyAmount(vestingData, "affiliateVesterClaimable", 18, 4, true)} MONEY</div>
                  </div>
                  <div className="App-card-divider" />
                  <div className="App-card-buttons m-0">
                    {!active && (
                      <Button variant="secondary" onClick={openConnectModal}>
                        <Trans>Connect Wallet</Trans>
                      </Button>
                    )}
                    {active && (
                      <Button variant="secondary" onClick={() => showAffiliateVesterDepositModal()}>
                        <Trans>Deposit</Trans>
                      </Button>
                    )}
                    {active && (
                      <Button variant="secondary" onClick={() => showAffiliateVesterWithdrawModal()}>
                        <Trans>Withdraw</Trans>
                      </Button>
                    )}
                    {active && (
                      <Button variant="secondary" onClick={() => showAffiliateVesterClaimModal()}>
                        <Trans>Claim</Trans>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )) ||
              null}
          </div>
        </div>
      </div>
      {/* <div className="mt-10">
        <PageTitle
          title={t`Incentives & Prizes`}
          subtitle={
            incentiveStats?.lp?.isActive || incentiveStats?.trading?.isActive ? (
              <Trans>Earn {incentivesToken} token incentives by purchasing GM tokens or trading in MONEY V2.</Trans>
            ) : (
              <Trans>Earn prizes by participating in MONEY Trading Competitions.</Trans>
            )
          }
        />
      </div>
      <UserIncentiveDistributionList /> */}
      <Footer />
    </div>
  );
}
